import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin } from "lucide-react";
import api from "@/lib/api";
import ProviderCard from "@/components/ProviderCard";
import ProvidersMap from "@/components/ProvidersMap";
import SEO from "@/components/SEO";

// Title-case from slug
const titleize = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

// Map URL slug → canonical service name in SERVICE_LIBRARY
const SLUG_TO_SERVICE = {
  "mobile-iv-therapy": "Hydration Drip",
  "hydration-therapy": "Hydration Drip",
  "nad-therapy": "NAD+ Infusion",
  "myers-cocktail": "Myers Cocktail",
  "immune-boost": "Immune Boost IV",
  "vitamin-c": "Vitamin C Megadose",
  "athletic-recovery": "Athletic Recovery",
  "hangover-recovery": "Hangover Recovery",
  "beauty-glow": "Beauty & Glow Drip",
  "weight-loss-iv": "Weight Loss IV",
  "iron-infusions": "Iron Infusion",
  "glutathione": "Glutathione Therapy",
  "jet-lag": "Jet Lag Recovery",
  "detox-therapy": "Detox Therapy",
  "migraine-relief": "Migraine Relief IV",
  "anti-aging": "Anti-Aging IV",
  "cold-flu": "Cold & Flu IV",
  "food-poisoning": "Food Poisoning IV",
  "energy-focus": "Energy & Focus IV",
  "b12": "B12 Injection",
  "magnesium": "Magnesium IV",
  "stress-serenity": "Stress & Serenity IV",
  "period-relief": "Period Relief IV",
  "morning-sickness": "Morning Sickness IV",
  "recovery": "Recovery IV",
  "performance-plus": "Performance Plus IV",
  "sunburn-recovery": "Sunburn Recovery IV",
  "vitamin-b-complex": "Vitamin B Complex IV",
  "wedding-glow": "Wedding Glow Drip",
};

export default function ServiceCityPage() {
  const { service, city } = useParams();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  const cityName = titleize(city);
  const serviceName = SLUG_TO_SERVICE[service] || titleize(service);

  useEffect(() => {
    setLoading(true);
    api.get(`/providers?service=${encodeURIComponent(serviceName)}&city=${encodeURIComponent(cityName)}&limit=50`)
      .then(({ data }) => setProviders(data.items))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [service, city, serviceName, cityName]);

  const title = `${serviceName} in ${cityName} — ${providers.length} Verified Providers | IV Therapy Finder`;
  const desc = `Find ${providers.length} verified providers offering ${serviceName} in ${cityName}. Read patient reviews, compare pricing, and book online.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: serviceName,
    areaServed: { "@type": "City", name: cityName },
    provider: providers.slice(0, 10).map((p) => ({
      "@type": "MedicalBusiness",
      name: p.name,
      address: { "@type": "PostalAddress", addressLocality: p.city, addressRegion: p.state },
      telephone: p.phone,
    })),
  };

  return (
    <div className="min-h-screen">
      <SEO title={title} description={desc} jsonLd={jsonLd} />
      <section className="bg-gradient-to-b from-[#F3F4F1] to-[#FAFAFA] border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/search" className="text-xs text-stone-500 hover:text-[#064E3B] inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-3 h-3" /> All services</Link>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 mb-3">
            {serviceName} in {cityName}
          </h1>
          <p className="text-stone-600 max-w-2xl">
            {providers.length} verified IV therapy {providers.length === 1 ? "provider" : "providers"} offering {serviceName} in {cityName}. Compare reviews, pricing, and book online.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>
        ) : providers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
            <p className="text-stone-500 mb-2">No providers offering {serviceName} in {cityName} yet.</p>
            <Link to={`/search?service=${encodeURIComponent(serviceName)}`} className="text-[#064E3B] font-semibold hover:underline text-sm">
              See all providers offering {serviceName} →
            </Link>
          </div>
        ) : (
          <>
            {(() => {
              const markers = providers
                .map((p) => {
                  const c = p.location?.coordinates;
                  if (!Array.isArray(c) || c.length < 2) return null;
                  return { id: p.id, name: p.name, slug: p.slug, city: p.city, state: p.state, lng: c[0], lat: c[1] };
                })
                .filter(Boolean);
              return markers.length > 0 ? (
                <div className="mb-6" data-testid="service-city-map">
                  <ProvidersMap markers={markers} height="380px" zoom={11} />
                </div>
              ) : null;
            })()}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {providers.map((p) => <ProviderCard key={p.id} p={p} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
